import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useUpdateVisitStatus } from "@/hooks/useVisitManagement";

interface Visit {
  id: string;
  visit_type: string;
  visit_number: number;
  scheduled_date: string;
}

interface MissedVisitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visit: Visit;
  onSuccess?: () => void;
}

const MISSED_REASONS = [
  { value: "no_show_no_contact", label: "Patient did not show up (no contact)" },
  { value: "cancelled_same_day", label: "Patient cancelled on the day" },
  { value: "patient_illness", label: "Patient reported illness" },
  { value: "transportation_issues", label: "Transportation issues" },
  { value: "work_schedule_conflict", label: "Work/schedule conflict" },
  { value: "family_emergency", label: "Family emergency" },
  { value: "forgot_appointment", label: "Patient forgot appointment" },
  { value: "weather_conditions", label: "Weather/road conditions" },
  { value: "other", label: "Other (specify below)" },
] as const;

export function MissedVisitDialog({ 
  open, 
  onOpenChange, 
  visit,
  onSuccess 
}: MissedVisitDialogProps) {
  const [reason, setReason] = useState<string>("");
  const [details, setDetails] = useState("");
  const { mutate: updateVisit, isPending } = useUpdateVisitStatus();

  const handleSubmit = () => {
    if (!reason) return;
    if (reason === "other" && details.trim().length < 5) return;

    const reasonLabel = MISSED_REASONS.find(r => r.value === reason)?.label || reason;
    const fullNotes = details.trim() 
      ? `${reasonLabel}: ${details.trim()}`
      : reasonLabel;

    updateVisit(
      {
        visitId: visit.id,
        status: 'missed',
        notes: fullNotes
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setReason("");
          setDetails("");
          onSuccess?.();
        }
      }
    );
  };

  const visitLabel = visit.visit_type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  const isValid = reason && (reason !== "other" || details.trim().length >= 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Mark Visit as Missed
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">
              <strong>{visitLabel}</strong> (Visit #{visit.visit_number})
            </p>
            <p className="text-sm text-muted-foreground">
              Scheduled: {format(new Date(visit.scheduled_date), 'MMMM d, yyyy')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for No-Show *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border z-50">
                {MISSED_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">
              Additional Details {reason === "other" ? "*" : "(Optional)"}
            </Label>
            <Textarea
              id="details"
              placeholder={reason === "other" 
                ? "Please describe the reason for the missed visit..." 
                : "Any additional context about why the visit was missed..."
              }
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
            />
            {reason === "other" && details.trim().length < 5 && (
              <p className="text-xs text-destructive">
                Please provide at least 5 characters when selecting "Other"
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleSubmit} 
            disabled={!isValid || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recording...
              </>
            ) : (
              "Mark as Missed"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
